import { ThemeEvents } from '@theme/events';

/**
 * Terms agreement checkbox. Disables add to cart until checked.
 */
class AgreementCheckboxComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const checkbox = this.#getCheckbox();
    if (!checkbox) return;

    checkbox.addEventListener('change', this.#updateButtonState, { signal });

    const scope = this.closest('.shopify-section, featured-product-information') ?? document;
    scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#updateButtonState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  #getCheckbox() {
    const input = this.querySelector('input[type="checkbox"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const inForm = this.closest('product-form-component');
    if (inForm instanceof HTMLElement) return inForm;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return null;

    return document.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
  }

  /** @returns {HTMLButtonElement[]} */
  #getAddToCartButtons() {
    const productForm = this.#getProductForm();
    if (!productForm) return [];

    return Array.from(
      productForm.querySelectorAll('add-to-cart-component button[type="submit"], button.add-to-cart-button[type="submit"]')
    ).filter((button) => button instanceof HTMLButtonElement);
  }

  /** @returns {HTMLElement | null} */
  #getAcceleratedCheckout() {
    const productForm = this.#getProductForm();
    return productForm?.querySelector('.accelerated-checkout-block') ?? null;
  }

  #updateButtonState = () => {
    const checkbox = this.#getCheckbox();
    const isChecked = Boolean(checkbox?.checked);

    this.dataset.agreementChecked = isChecked ? 'true' : 'false';

    for (const button of this.#getAddToCartButtons()) {
      if (isChecked) {
        if (button.dataset.disabledByAgreement === 'true') {
          button.disabled = false;
          delete button.dataset.disabledByAgreement;
        }
      } else if (!button.disabled || button.dataset.disabledByAgreement === 'true') {
        button.dataset.disabledByAgreement = 'true';
        button.disabled = true;
      }
    }

    const acceleratedCheckout = this.#getAcceleratedCheckout();
    if (acceleratedCheckout) {
      acceleratedCheckout.toggleAttribute('inert', !isChecked);
      acceleratedCheckout.style.opacity = isChecked ? '' : '0.5';
      acceleratedCheckout.style.pointerEvents = isChecked ? '' : 'none';
    }
  };

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const selector = `agreement-checkbox-component[data-block-id="${blockId}"]`;
    const newElement = event.detail?.data?.html?.querySelector(selector);

    if (!(newElement instanceof HTMLElement) || newElement.isEqualNode(this)) return;

    const wasChecked = this.#getCheckbox()?.checked ?? false;
    this.replaceWith(newElement);

    const newCheckbox = newElement.querySelector('input[type="checkbox"]');
    if (newCheckbox instanceof HTMLInputElement && wasChecked) {
      newCheckbox.checked = wasChecked;
    }
  };
}

if (!customElements.get('agreement-checkbox-component')) {
  customElements.define('agreement-checkbox-component', AgreementCheckboxComponent);
}
